import { BaseRepository } from './BaseRepository';
import { AxiosPromise } from 'axios';
import AuthStore from '@app/src/common/store/AuthStore';
import {
  ISelectedContentItemFoldersResponse,
  IShareContentDto,
  ISharedItemData
} from '@app/src/common/model/SharedContent';
import {
  IOneDriveFolder,
  IFolder
} from '@app/src/components/file-storage/model/Folder';
import {
  IExternalStorageFolder,
  IExternalStorageFolderDto
} from '@app/src/components/file-storage/model/ExternalStorage';

export interface IMoveGalleryFolderDto {
  ids: string[];
  newParentId: string;
}

export interface IFileMeta {
  id: string;
  folderId: string | null;
  name: string;
  contentType: string;
  contentLength: number;
  partLength: number;
  thumbnailUrl: string;
  downloadUrl: string;
  previewUrl: string;
  parts: IFilePart[];
  createdDate: string;
  createdByUserName: string;
  isShared?: boolean;
  lastSyncTimestamp?: string;
}

export interface IFolderRequestParams {
  startIndex?: number;
  endIndex?: number;
  total?: number;
  sort?: string;
  search?: string;
}

export interface IFilePart {
  start: number;
  end: number;
  checksum: string;
}

interface IFileParams {
  fileId?: string;
  name: string;
  contentType: string;
  contentLength: number;
  singlePart?: boolean;
  companyId?: string;
  folderId?: string;
}

export interface IFolderCreate {
  id?: string;
  parentId: string;
  name: string;
}

export interface IFolderContent {
  id: string;
  folders: IFolder[];
  files: IFileMeta[];
  totalItems: number;
}

interface IGetOneDriveFoldersParams {
  credentialId: string;
  siteId?: string;
  parentFolderId?: string;
}

interface IFileReuploadResponse {
  usedInPlaylists: boolean;
}

interface ISharepointRootDriveResponse {
  id: string;
  name: string;
}

class FilesRepository extends BaseRepository {
  protected filesUrl: string = `${this.fileStorageUrl}/files`;
  protected foldersUrl: string = `${this.fileStorageUrl}/folders`;

  getFileMeta(id: string) {
    return this.callGet<IFileMeta>(`${this.filesUrl}/${id}`);
  }

  getFilesMeta(filesIds: string) {
    return this.callGet<IFileMeta[]>(`${this.filesUrl}`, { fileIds: filesIds });
  }

  async saveFile(file: File, id: string) {
    const data = new FormData();
    data.append('file', file);
    const fileId = await this.callPut<string>(
      `${this.filesUrl}/${id}/upload`,
      data
    );
    return fileId.data;
  }

  createImageFile = ({ name, type, size }: File) =>
    this.callPost<string>(this.filesUrl, {
      name,
      contentType: type,
      contentLength: size,
      singlePart: true
    });

  updateImageFile = ({ name, type, size }: File, fileId: string) => {
    return this.callPut<string>(this.filesUrl, {
      fileId,
      name,
      contentType: type,
      contentLength: size
    });
  };

  updateFileName = (file: IFileMeta) => {
    return this.callPut<string>(`${this.filesUrl}/${file.id}/rename`, {
      name: file.name
    });
  };

  createFile = async ({ name, type, size }: File, folderId?: string) => {
    const params: IFileParams = {
      name,
      contentType: type,
      contentLength: size
    };
    if (folderId) {
      params.folderId = folderId;
    }
    if (AuthStore.user) {
      params.companyId = AuthStore.companyId;
    }

    const response = await this.callPost<string>(this.filesUrl, params);
    return response.data;
  };

  updateFile = async (fileId: string, { name, type, size }: File) => {
    const params: IFileParams = {
      fileId,
      name,
      contentType: type,
      contentLength: size
    };
    const response = await this.callPut<IFileReuploadResponse>(
      `${this.filesUrl}?checkUsage=true`,
      params
    );
    return response.data;
  };

  deleteFileFromGallery = (id: string) =>
    this.callDelete(`${this.filesUrl}/${id}/fromGallery`);

  getFolderContent(
    folderId: string,
    params?: IFolderRequestParams
  ): AxiosPromise<IFolderContent> {
    return this.callGet(`${this.foldersUrl}/${folderId}/content`, params);
  }

  getFolders(): AxiosPromise<IFolder[]> {
    return this.callGet<IFolder[]>(this.foldersUrl);
  }

  createFolder(folder: IFolderCreate): AxiosPromise<string> {
    return this.callPost(this.foldersUrl, folder);
  }

  deleteFolder(folderId: string): AxiosPromise<null> {
    return this.callDelete(`${this.foldersUrl}/${folderId}`);
  }

  updateFolder(folder: IFolderCreate): AxiosPromise<null> {
    return this.callPost(this.foldersUrl, folder);
  }

  updateFileFolder = (id: string, folderId: string) => {
    return this.callPut<string>(`${this.filesUrl}/${id}/changeFolder`, {
      folderId
    });
  };

  shareGalleryItem = (id: string, body: IShareContentDto) => {
    return this.callPut(`${this.filesUrl}/${id}/share`, body);
  };

  shareGalleryFolder = (id: string, body: IShareContentDto) => {
    return this.callPut(`${this.foldersUrl}/${id}/share`, body);
  };

  getSharedFolderData = (id: string) => {
    return this.callGet<ISharedItemData>(`${this.foldersUrl}/${id}/share`);
  };

  getSharedItemData = (id: string) => {
    return this.callGet<ISharedItemData>(`${this.filesUrl}/${id}/share`);
  };

  moveFolder(movableFolderId: string, newParentId: string): AxiosPromise<null> {
    return this.callPut(`${this.foldersUrl}/move`, {
      ids: [movableFolderId],
      newParentId
    } as IMoveGalleryFolderDto);
  }

  getSharedFoldersIds = (
    id: string
  ): AxiosPromise<ISelectedContentItemFoldersResponse> => {
    return this.callGet<ISelectedContentItemFoldersResponse>(
      `${this.filesUrl}/${id}/folders`
    );
  };

  bulkDeleteFiles = (ids: string[]) => {
    return this.callDelete(`${this.filesUrl}/ids/fromGallery`, { ids });
  };

  getOneDriveFolders = (
    params: IGetOneDriveFoldersParams
  ): AxiosPromise<IOneDriveFolder[]> => {
    return this.callGet<IOneDriveFolder[]>(
      `${this.fileStorageUrl}/OneDrive/folders`,
      params
    );
  };

  getSharepointSites = (
    params: IGetOneDriveFoldersParams
  ): AxiosPromise<IOneDriveFolder[]> => {
    return this.callGet<IOneDriveFolder[]>(
      `${this.fileStorageUrl}/sharepoint/sites`,
      params
    );
  };

  getSharepointRootDrive = (
    params: IGetOneDriveFoldersParams
  ): AxiosPromise<IOneDriveFolder> => {
    return this.callGet<IOneDriveFolder>(
      `${this.fileStorageUrl}/sharepoint/root-drive`,
      params
    );
  };

  createExternalStorageFolder = (body: IExternalStorageFolderDto) => {
    return this.callPost(`${this.foldersUrl}/ext-storage`, body);
  };

  updateExternalStorageFolder = (
    id: string,
    body: IExternalStorageFolderDto
  ) => {
    return this.callPut(`${this.foldersUrl}/${id}/ext-storage`, body);
  };

  syncExternalFolder = (id: string) => {
    return this.callPost(`${this.foldersUrl}/${id}/ext-storage-sync`);
  };

  getExternalStorageFolder = (id: string) => {
    return this.callGet<IExternalStorageFolder>(
      `${this.foldersUrl}/${id}/ext-storage`
    );
  };
}

export default new FilesRepository();
